/* eslint-disable react/no-unescaped-entities */
/**
 * Publicly accessible Terms of Service — no auth required.
 * Content mirrors /app/memory/legal/TERMS_OF_SERVICE.md.
 */
import LegalLayout from "@/components/legal/LegalLayout";

export default function Terms() {
  return (
    <LegalLayout
      testid="terms-page"
      eyebrow="Terms"
      title="HOH Solfa Studio — Terms of Service"
      effective="July 2026"
      publisher="HOH VIBES"
      contact="support@hohvibes.com"
      website="https://hohvibes.com"
    >
      <p>
        These Terms of Service ("Terms") govern your access to and use of the HOH Solfa Studio
        mobile application, website and related services (the "Service") provided by HOH VIBES
        ("we", "us", "our"). By using the Service you agree to be bound by these Terms.
      </p>

      <h2>1. Eligibility</h2>
      <p>
        You must be at least 13 years old, or the minimum digital-consent age in your country, to
        use the Service. If you are under 18 you confirm that a parent or guardian agrees to these
        Terms on your behalf.
      </p>

      <h2>2. Your account</h2>
      <ul>
        <li>You are responsible for keeping your sign-in credentials secure and for all activity under your account.</li>
        <li>Please notify us immediately at <a href="mailto:support@hohvibes.com">support@hohvibes.com</a> if you suspect unauthorised access.</li>
        <li>We may suspend or terminate an account that violates these Terms, applicable law, or the rights of other users.</li>
      </ul>

      <h2>3. What the Service offers</h2>
      <p>HOH Solfa Studio is a digital hymnbook and choir-rehearsal platform. It includes:</p>
      <ul>
        <li>A searchable library of hymns with lyrics in multiple languages.</li>
        <li>Interactive music notation, Tonic Sol-fa and SATB harmony where available.</li>
        <li>Audio playback (MIDI and MP3) for eligible hymns.</li>
        <li>Personal favourites, folders and (paid tiers) offline downloads.</li>
      </ul>
      <p>Content availability may change over time as we curate and improve the library.</p>

      <h2>4. Free and paid plans</h2>
      <ul>
        <li><strong>Free plan</strong> — grants access to lyrics, search, browse-by-collection, and multi-language display.</li>
        <li><strong>Basic (£5, one-time)</strong> — unlocks Tonic Sol-fa, SATB, MIDI/MP3 playback and offline downloads.</li>
        <li><strong>Musician (£8, one-time — currently unavailable)</strong> — reserved for future release.</li>
      </ul>
      <p>
        Prices are shown in GBP and include VAT where applicable. Payments on iOS are processed
        by Apple and governed by Apple's App Store Terms. Payments on the web are processed by
        Stripe. Purchases are one-time and non-recurring — no subscription auto-renews.
      </p>

      <h2>5. Refunds</h2>
      <ul>
        <li><strong>iOS purchases:</strong> refund requests are handled by Apple. Contact Apple Support at <a href="https://reportaproblem.apple.com/" target="_blank" rel="noreferrer noopener">https://reportaproblem.apple.com/</a>.</li>
        <li><strong>Web / Stripe purchases:</strong> contact <a href="mailto:support@hohvibes.com">support@hohvibes.com</a> within 14 days of purchase for a full refund, provided you have not extensively used the premium features.</li>
      </ul>

      <h2>6. Restore purchases</h2>
      <p>
        If you reinstall the app or sign in on a new device, tap <strong>Restore Purchases</strong> on
        the Pricing screen (iOS only) to re-attach a previous purchase to the current account. On the
        web, purchases attach automatically to your signed-in account.
      </p>

      <h2>7. Acceptable use</h2>
      <p>You agree not to:</p>
      <ul>
        <li>Copy, redistribute, sell, sublicense, publicly perform for commercial gain, or create derivative works from any content in the Service unless you own that content and have granted us permission, or the content is in the public domain.</li>
        <li>Reverse-engineer, decompile, or otherwise attempt to extract the Service's source code, other than to the extent allowed by law.</li>
        <li>Use the Service to violate any law or infringe anyone's rights.</li>
        <li>Interfere with, disrupt, or attempt to gain unauthorised access to the Service or its infrastructure.</li>
      </ul>

      <h2>8. Content ownership</h2>
      <p>
        Hymn lyrics that are in the public domain (published pre-1929 in the UK, pre-1928 in the US,
        or otherwise out of copyright in your jurisdiction) are provided as a service of scholarship
        and worship. Modern arrangements, our proprietary notation renderer, our multi-language
        translations that we have commissioned, and the app's design and code, remain the property of
        HOH VIBES and its licensors.
      </p>
      <p>
        Admin-uploaded content contributed to your church's library remains the property of your
        church or the original creator; you grant HOH VIBES a non-exclusive, worldwide licence to
        store and display it inside your own tenant of the Service.
      </p>

      <h2>9. Termination</h2>
      <p>
        You may delete your account at any time from within the app (<strong>Profile → Delete my
        account</strong>). We may terminate or suspend your account with reasonable notice for
        material breach of these Terms. On termination:
      </p>
      <ul>
        <li>Your access to premium features ends immediately.</li>
        <li>Your personal data is deleted or anonymised as described in our Privacy Policy.</li>
      </ul>

      <h2>10. Disclaimers</h2>
      <p>
        The Service is provided "as is" and "as available". To the maximum extent permitted by law,
        HOH VIBES disclaims all implied warranties of merchantability, fitness for a particular
        purpose, and non-infringement. We do not warrant that the Service will be uninterrupted,
        error-free or completely secure.
      </p>

      <h2>11. Limitation of liability</h2>
      <p>
        To the maximum extent permitted by law, HOH VIBES's total aggregate liability arising out
        of or related to these Terms or your use of the Service will not exceed the greater of £50
        or the amount you have paid us in the 12 months preceding the claim. Nothing in these Terms
        limits liability that cannot be limited by law (e.g. death or personal injury caused by
        negligence).
      </p>

      <h2>12. Governing law</h2>
      <p>
        These Terms are governed by the laws of England and Wales. Any dispute will be subject to
        the exclusive jurisdiction of the courts of England and Wales, without prejudice to any
        mandatory consumer-protection law of your country of residence.
      </p>

      <h2>13. Changes</h2>
      <p>
        We may update these Terms from time to time. Material changes will be announced at{" "}
        <a href="https://hohvibes.com/terms">https://hohvibes.com/terms</a> and via an in-app
        message. Continued use after the effective date means you accept the changes.
      </p>

      <h2>14. Contact</h2>
      <p>For any questions about these Terms, contact us at:</p>
      <p>
        <strong>HOH VIBES</strong><br />
        Email: <a href="mailto:support@hohvibes.com">support@hohvibes.com</a><br />
        Web: <a href="https://hohvibes.com">https://hohvibes.com</a>
      </p>
    </LegalLayout>
  );
}
